import { TALK_TIME_BUCKETS, emptyTalkTime, talkTimeAtLeast } from '@marnic/shared';
import type { AnsweredTalkTime, TalkTimeBucketKey } from '@marnic/shared';

// Short → long, cool → warm-green: the ramp reads as "how much of a real
// conversation this was", so a bar dominated by grey is instantly a bad sign.
const BUCKET_COLORS: Record<TalkTimeBucketKey, string> = {
  under20s: '#cbd5e1',
  from20sTo1m: '#fbbf24',
  from1mTo2m: '#93c5fd',
  from2mTo3m: '#60a5fa',
  from3mTo5m: '#34d399',
  from5mTo10m: '#10b981',
  over10m: '#047857',
};

/** The bands people quote as thresholds — "20s+", "1m+", "3m+", "5m+". */
export const CUMULATIVE_BUCKETS = TALK_TIME_BUCKETS.filter((b) => b.minSeconds > 0);

export function TalkTimeBreakdown({ buckets, title = 'Answered by talk time' }: { buckets?: AnsweredTalkTime; title?: string }): JSX.Element {
  const data = buckets ?? emptyTalkTime();
  const total = TALK_TIME_BUCKETS.reduce((sum, b) => sum + data[b.key], 0);
  return (
    <section className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink-700">{title}</h2>
        <span className="text-xs tabular-nums text-ink-500">{total.toLocaleString()} answered</span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-ink-500">No answered calls in this range.</p>
      ) : (
        <>
          <div
            className="flex h-3 w-full overflow-hidden rounded-full bg-ink-100"
            role="img"
            aria-label={TALK_TIME_BUCKETS.map((b) => `${b.label}: ${data[b.key]}`).join(', ')}
          >
            {TALK_TIME_BUCKETS.map((b) => (
              data[b.key] > 0 ? (
                <div
                  key={b.key}
                  style={{ width: `${(data[b.key] / total) * 100}%`, backgroundColor: BUCKET_COLORS[b.key] }}
                  title={`${b.label}: ${data[b.key]} (${percentOf(data[b.key], total)})`}
                />
              ) : null
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
            {TALK_TIME_BUCKETS.map((b) => (
              <div key={b.key} className="rounded-lg border border-ink-100 p-2">
                <div className="flex items-center gap-1.5 text-xs text-ink-500">
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: BUCKET_COLORS[b.key] }} />
                  {b.label}
                </div>
                <div className="mt-1 text-lg font-semibold tabular-nums text-ink-800">{data[b.key].toLocaleString()}</div>
                <div className="text-xs tabular-nums text-ink-500">{percentOf(data[b.key], total)} of answered</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-ink-100 pt-3 text-sm">
            <span className="text-xs uppercase tracking-wide text-ink-500">Talked at least</span>
            {CUMULATIVE_BUCKETS.map((b) => {
              const value = talkTimeAtLeast(data, b.key);
              return (
                <span key={b.key} className="flex items-baseline gap-1.5">
                  <span className="text-ink-500">{b.atLeastLabel}</span>
                  <span className="font-semibold tabular-nums text-ink-800">{value.toLocaleString()}</span>
                  <span className="text-xs tabular-nums text-ink-500">({percentOf(value, total)})</span>
                </span>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

export function percentOf(value: number, total: number): string {
  if (!total) return '0%';
  const pct = (value / total) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
}
